// /var/www/afra/web-design.rampagesoft.com/my-app/src/page/Content.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BreadCrumb } from 'primereact/breadcrumb';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Paginator } from 'primereact/paginator';
import { fetchPosts, fetchPostImage } from '../services/api';
import '../css/Content.scss';
import 'primeicons/primeicons.css';

// Utility functions (copied from ContentDetail.js)
const stripHtml = (html) => {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  return tempDiv.textContent || tempDiv.innerText || '';
};

const truncateText = (text, maxLength) => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

const createUrlFriendlyString = (text) => {
  return encodeURIComponent(text)
    .replace(/%20/g, '-')
    .replace(/%[0-9A-F]{2}/g, match => {
      return match === '%2F' ? '/' : match;
    });
};

// Utility function to format date to "DD MMM YYYY" (e.g., "21 Apr 2025")
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

// Utility function to determine the correct image URL
const getImageUrl = (imgValue, defaultImage) => {
  if (!imgValue) return defaultImage;

  if (imgValue.startsWith('http://') || imgValue.startsWith('https://')) {
    return imgValue;
  }

  return `https://maracar.rampagesoft.com/model/upload/img/IMG_Home/${imgValue}`;
};

export default function Content() {
  const [posts, setPosts] = useState([]);
  const [images, setImages] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [first, setFirst] = useState(0);
  const rowsPerPage = 15;
  const defaultImage = 'https://afaa.website/s/ff6970.png';

  useEffect(() => {
    async function fetchContentList() {
      try {
        setLoading(true);
        setError(null);

        const offset = currentPage * rowsPerPage;
        const data = await fetchPosts(rowsPerPage, offset);
        const fetchedPosts = data.results || [];
        setPosts(fetchedPosts);
        setTotalRecords(data.total_count || 0);

        const imageResults = await Promise.all(
          fetchedPosts.map(async post => {
            let img = defaultImage;
            try {
              const imageData = await fetchPostImage(post.Home_ID);
              const rawImg = imageData.results?.[0]?.ImgHome_Img || defaultImage;
              img = getImageUrl(rawImg, defaultImage);
            } catch (imgError) {
              console.warn(`Failed to fetch image for Home_ID ${post.Home_ID}: ${imgError.message}`);
            }
            return { id: post.Home_ID, imgUrl: img };
          })
        );
        setImages(Object.fromEntries(imageResults.map(r => [r.id, r.imgUrl])));
      } catch (e) {
        console.error('Failed to fetch content list:', e.message);
        setError('Unable to load content. Please try again later.');
      } finally {
        setLoading(false);
      }
    }
    fetchContentList();
  }, [currentPage]);

  const onPageChange = (event) => {
    setFirst(event.first);
    setCurrentPage(event.page);
    window.scrollTo(0, 0);
  };

  if (loading) return <div className="content__loading"><ProgressSpinner /></div>;
  if (error) return <div className="content__error">{error}</div>;
  if (posts.length === 0) return <div className="content__empty">No content available</div>;

  const breadcrumbItems = [
    { label: 'Home', url: '/', icon: 'pi pi-home' },
    { label: 'Content', url: '/content', icon: 'pi pi-folder', disabled: true },
  ];

  return (
    <section>
      <div data-mode="container">
        <BreadCrumb model={breadcrumbItems} className="content__breadcrumb" />
        <div className="content">
          {posts.map(post => {
            const cleanedTitle = stripHtml(post.Home_Sub || 'Untitled');
            const truncatedTitle = truncateText(cleanedTitle, 50);
            const cleanedDescription = stripHtml(post.Home_Short || '');
            const truncatedDescription = truncateText(cleanedDescription, 200);
            const urlFriendlyName = createUrlFriendlyString(cleanedTitle);

            return (
              <article key={post.Home_ID} className="content__item">
                <Link to={`/content/${post.Home_ID}/${urlFriendlyName}`}>
                  {images[post.Home_ID] && (
                    <figure className="content__image-container">
                      <img
                        src={images[post.Home_ID]}
                        alt={post.Home_Sub || 'Content Image'}
                        className="content__image"
                        onError={() => console.warn(`Failed to load image: ${images[post.Home_ID]}`)}
                      />
                    </figure>
                  )}
                  <header className="content__header">
                    <h2 className="content__title">{truncatedTitle}</h2>
                    <div className="content__meta">
                      <span className="content__meta-item">
                        <i className="pi pi-calendar"></i>
                        {formatDate(post.Home_Date)}
                      </span>
                      <span className="content__meta-item">
                        <i className="pi pi-eye"></i>
                        {post.Home_Visitors}
                      </span>
                    </div>
                  </header>
                  <section className="content__summary">
                    <p>{truncatedDescription}</p>
                  </section>
                </Link>
              </article>
            );
          })}
        </div>
        <Paginator
          first={first}
          rows={rowsPerPage}
          totalRecords={totalRecords}
          onPageChange={onPageChange}
          className="content__paginator"
        />
      </div>
    </section>
  );
}